import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { User, UserTier, Organization, TierProgressInfo, FeatureFlag } from '../types/tier';
import { useTierApi } from '../services/tierApi';

export interface ImpersonationContext {
  originalUserId: string;
  impersonatedUserId: string;
  sessionId: string;
  startedAt: string;
}

export const useTierAuthStore = defineStore('tierAuth', () => {
  // State
  const user = ref<User | null>(null);
  const organizations = ref<Organization[]>([]);
  const featureFlags = ref<FeatureFlag[]>([]);
  const tierProgress = ref<TierProgressInfo | null>(null);
  const impersonationContext = ref<ImpersonationContext | null>(null);
  const loading = ref(false);
  const error = ref<string | null>(null);
  const isInitialized = ref(false);

  // API instance
  const tierApi = useTierApi();

  // Getters
  const isAuthenticated = computed(() => !!user.value);

  const currentTier = computed(() => user.value?.tier ?? UserTier.TIER_0);

  const isAdmin = computed(() => {
    return user.value?.role === 'admin' || user.value?.role === 'super_admin';
  });

  const isSuperAdmin = computed(() => {
    return user.value?.role === 'super_admin';
  });

  const isImpersonating = computed(() => !!impersonationContext.value);

  const impersonatedUser = computed(() => {
    if (!isImpersonating.value) return null;
    return user.value; // This is the impersonated user
  });

  const originalUser = computed(() => {
    if (!isImpersonating.value) return user.value;
    // In real implementation, we'd fetch the original user data
    return null;
  });

  const canAccessFeature = computed(() => (featureKey: string) => {
    const flag = featureFlags.value.find(f => f.key === featureKey);
    if (!flag || !flag.enabled) return false;

    // Check tier restriction
    if (flag.tierRestriction && currentTier.value < flag.tierRestriction) {
      return false;
    }

    // Check organization restriction
    if (flag.organizationRestriction && user.value?.organizationId) {
      if (!flag.organizationRestriction.includes(user.value.organizationId)) {
        return false;
      }
    }

    // Check user restriction
    if (flag.userRestriction && user.value) {
      if (!flag.userRestriction.includes(user.value.id)) {
        return false;
      }
    }

    return true;
  });

  const hasPermission = computed(() => (resource: string, action: string) => {
    if (!user.value) return false;

    // Super admin has all permissions
    if (user.value.role === 'super_admin') return true;

    // Define tier-based permissions
    const tierPermissions: Record<UserTier, string[]> = {
      [UserTier.TIER_0]: ['bots:read'],
      [UserTier.TIER_1]: ['bots:read', 'bots:write'],
      [UserTier.TIER_2]: ['bots:read', 'bots:write', 'analytics:read'],
      [UserTier.TIER_3]: [
        'bots:read',
        'bots:write',
        'bots:delete',
        'analytics:read',
        'organizations:read',
      ],
      [UserTier.TIER_4]: [
        'bots:read',
        'bots:write',
        'bots:delete',
        'analytics:read',
        'analytics:write',
        'organizations:read',
        'organizations:write',
        'payments:read',
      ],
      [UserTier.TIER_5]: [
        'bots:read',
        'bots:write',
        'bots:delete',
        'analytics:read',
        'analytics:write',
        'organizations:read',
        'organizations:write',
        'payments:read',
        'payments:write',
      ],
      [UserTier.TIER_6]: [
        'bots:read',
        'bots:write',
        'bots:delete',
        'analytics:read',
        'analytics:write',
        'organizations:read',
        'organizations:write',
        'organizations:admin',
        'payments:read',
        'payments:write',
      ],
      [UserTier.TIER_7]: [
        'bots:read',
        'bots:write',
        'bots:delete',
        'analytics:read',
        'analytics:write',
        'organizations:read',
        'organizations:write',
        'organizations:admin',
        'payments:read',
        'payments:write',
        'feature_flags:read',
      ],
    };

    const userPermissions = tierPermissions[user.value.tier] || [];
    const permission = `${resource}:${action}`;

    return (
      userPermissions.includes(permission) ||
      userPermissions.includes(`${resource}:admin`) ||
      (user.value.role === 'admin' && ['users', 'organizations', 'analytics'].includes(resource))
    );
  });

  // Actions
  const setToken = (token: string) => {
    tierApi.setToken(token);
  };

  const removeToken = () => {
    tierApi.removeToken();
  };

  const loadUser = async (userId?: string) => {
    if (!userId && !user.value?.id) return;

    loading.value = true;
    error.value = null;

    try {
      const userData = await tierApi.getUser(userId || user.value!.id);
      user.value = userData;

      // Load tier progress
      if (userData.id) {
        tierProgress.value = await tierApi.getUserTierProgress(userData.id);
      }

      // Load user's organizations
      if (userData.organizationId) {
        const orgData = await tierApi.getOrganization(userData.organizationId);
        organizations.value = orgData ? [orgData] : [];
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to load user';
      console.error('Failed to load user:', err);
    } finally {
      loading.value = false;
    }
  };

  const updateUser = async (updates: Partial<User>) => {
    if (!user.value) return;

    loading.value = true;
    error.value = null;

    try {
      const updatedUser = await tierApi.updateUser(user.value.id, updates);
      user.value = updatedUser;

      // Reload tier progress
      tierProgress.value = await tierApi.getUserTierProgress(updatedUser.id);
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to update user';
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const upgradeTier = async () => {
    if (!user.value) return;

    loading.value = true;
    error.value = null;

    try {
      const result = await tierApi.upgradeTier(user.value.id);

      if (result.success) {
        // Reload user data
        await loadUser();
        return result;
      } else {
        throw new Error(result.message);
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to upgrade tier';
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const verifyEmail = async (code: string) => {
    loading.value = true;
    error.value = null;

    try {
      const result = await tierApi.verifyEmail({ action: 'verify', code });

      if (result.success && user.value) {
        user.value.emailVerified = true;
        // Reload tier progress
        tierProgress.value = await tierApi.getUserTierProgress(user.value.id);
      }

      return result;
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to verify email';
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const verifyPhone = async (code: string) => {
    loading.value = true;
    error.value = null;

    try {
      const result = await tierApi.verifyPhone({ action: 'verify', code });

      if (result.success && user.value) {
        user.value.phoneVerified = true;
        // Reload tier progress
        tierProgress.value = await tierApi.getUserTierProgress(user.value.id);
      }

      return result;
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to verify phone';
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const verifyIdentity = async (data: any) => {
    loading.value = true;
    error.value = null;

    try {
      const result = await tierApi.verifyIdentity(data);

      if (result.success && user.value) {
        // Update user's identity verification status
        if (!user.value.identityVerification) {
          user.value.identityVerification = {
            documentType: data.documentType,
            documentNumber: data.documentNumber,
            documentCountry: data.documentCountry,
            fullName: data.fullName,
            dateOfBirth: data.dateOfBirth,
            verified: true,
            verifiedAt: new Date().toISOString(),
            verificationProvider: 'manual',
            documentImageUrls: [],
          };
        } else {
          user.value.identityVerification.verified = true;
          user.value.identityVerification.verifiedAt = new Date().toISOString();
        }

        // Reload tier progress
        tierProgress.value = await tierApi.getUserTierProgress(user.value.id);
      }

      return result;
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to verify identity';
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const loadFeatureFlags = async () => {
    if (!isAdmin.value) return;

    try {
      const result = await tierApi.getFeatureFlags();
      featureFlags.value = result.featureFlags;
    } catch (err) {
      console.error('Failed to load feature flags:', err);
    }
  };

  const updateFeatureFlag = async (key: string, enabled: boolean) => {
    if (!isAdmin.value) return;

    try {
      await tierApi.updateFeatureFlag(key, enabled);
      const flag = featureFlags.value.find(f => f.key === key);
      if (flag) {
        flag.enabled = enabled;
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to update feature flag';
      throw err;
    }
  };

  const startImpersonation = async (targetUserId: string) => {
    if (!isAdmin.value) return;

    loading.value = true;
    error.value = null;

    try {
      const result = await tierApi.startImpersonation(targetUserId);

      // Set the new token
      setToken(result.token);

      // Update impersonation context
      impersonationContext.value = {
        originalUserId: user.value!.id,
        impersonatedUserId: targetUserId,
        sessionId: result.token, // Simplified - should extract from token
        startedAt: new Date().toISOString(),
      };

      // Load the impersonated user
      await loadUser(targetUserId);
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to start impersonation';
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const endImpersonation = async () => {
    if (!impersonationContext.value) return;

    loading.value = true;
    error.value = null;

    try {
      await tierApi.endImpersonation(impersonationContext.value.sessionId);

      // Clear impersonation context
      const originalUserId = impersonationContext.value.originalUserId;
      impersonationContext.value = null;

      // Reload original user
      await loadUser(originalUserId);
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to end impersonation';
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const logout = () => {
    user.value = null;
    organizations.value = [];
    featureFlags.value = [];
    tierProgress.value = null;
    impersonationContext.value = null;
    error.value = null;
    isInitialized.value = false;
    removeToken();
  };

  const initialize = async (token?: string) => {
    if (token) {
      setToken(token);
    }

    isInitialized.value = true;

    // If we have a token, try to load user data
    if (token) {
      try {
        // Extract user ID from token or try to get current user
        // This is simplified - in real implementation, decode token or call /me endpoint
        await loadUser();
      } catch (err) {
        console.error('Failed to initialize user:', err);
        logout();
      }
    }
  };

  return {
    // State
    user: user,
    organizations: organizations,
    featureFlags: featureFlags,
    tierProgress: tierProgress,
    impersonationContext: impersonationContext,
    loading: loading,
    error: error,
    isInitialized: isInitialized,

    // Getters
    isAuthenticated,
    currentTier,
    isAdmin,
    isSuperAdmin,
    isImpersonating,
    impersonatedUser,
    originalUser,
    canAccessFeature,
    hasPermission,

    // Actions
    setToken,
    removeToken,
    loadUser,
    updateUser,
    upgradeTier,
    verifyEmail,
    verifyPhone,
    verifyIdentity,
    loadFeatureFlags,
    updateFeatureFlag,
    startImpersonation,
    endImpersonation,
    logout,
    initialize,
  };
});
